import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../../ui/card';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GeneralDataView = ({ formData, setFormData, materialTypeInfo, readOnly = false }) => {
  const [industrySectors, setIndustrySectors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadIndustrySectors();
  }, []);

  const loadIndustrySectors = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/industry-sectors`);
      setIndustrySectors(response.data || []);
    } catch (error) {
      console.error('Error loading industry sectors:', error);
      toast.error('Failed to load industry sectors');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      general_data: {
        ...prev.general_data,
        [field]: value
      }
    }));
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-3">
        <h3 className="text-lg font-semibold text-gray-900">General Data</h3>
        <p className="text-sm text-gray-600 mt-1">
          Basic identification and classification information
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Material Number (Read-only) */}
        <div>
          <Label>Material Number *</Label>
          <Input
            type="text"
            value={materialTypeInfo.materialNumber}
            disabled
            className="bg-gray-50"
          />
          <p className="text-xs text-gray-500 mt-1">Will be assigned when material is saved</p>
        </div>

        {/* Industry Sector */}
        <div>
          <Label>Industry Sector *</Label>
          <div className="relative">
            <select
              className={`w-full mt-1 px-3 py-2.5 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none text-base ${readOnly ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
              value={formData.general_data?.industry_sector || ''}
              onChange={(e) => handleChange('industry_sector', e.target.value)}
              required
              disabled={readOnly}
            >
              <option value="">Select Industry Sector</option>
              {industrySectors.map(sector => (
                <option key={sector.industry_sector_id} value={sector.industry_sector_id}>
                  {sector.industry_sector_id} - {sector.description}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
              <svg className="h-4 w-4 fill-current" viewBox="0 0 20 20">
                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">Select the industry sector for this material</p>
        </div>

        {/* Material Type (Read-only) */}
        <div>
          <Label>Material Type *</Label>
          <Input
            type="text"
            value={`${materialTypeInfo.materialType.material_type_id} - ${materialTypeInfo.materialType.description}`}
            disabled
            className="bg-gray-50"
          />
          <p className="text-xs text-gray-500 mt-1">Selected material type</p>
        </div>

        {/* Material Type Description (Read-only) */}
        <div>
          <Label>Material Type Description</Label>
          <Input
            type="text"
            value={materialTypeInfo.materialType.description}
            disabled
            className="bg-gray-50"
          />
        </div>
      </div>

      <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
        <div className="flex items-start gap-2">
          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-orange-600 text-white flex items-center justify-center text-xs font-bold mt-0.5">
            i
          </div>
          <div>
            <p className="text-sm font-medium text-orange-900 mb-1">About General Data</p>
            <p className="text-sm text-orange-800">
              Material number and type are automatically set based on your earlier selection. 
              Industry sector is a required field for classification purposes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralDataView;
